import {
  AppstoreOutlined,
  BarcodeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InboxOutlined,
  PercentageOutlined,
  StarOutlined,
  TagOutlined,
} from '@ant-design/icons';
import {Modal, Tag} from 'antd';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';

const InfoRow = ({icon, label, value, className = ''}) => (
  <div className={`flex items-start gap-2 ${className}`}>
    <span className={'text-primary-700 mt-0.5 flex-shrink-0'}>{icon}</span>
    <div className={'min-w-0'}>
      <p className={'font-medium uppercase tracking-wide leading-none mb-0.5'}>{label}</p>
      <p className={'font-medium break-words'}>{value || '—'}</p>
    </div>
  </div>
);
InfoRow.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  className: PropTypes.string,
};

const ProductDetailScreen = ({viewModal, setViewModal}) => {
  const record = viewModal.record;

  useEffect(() => {
    if (!viewModal.visible) {return;}
  }, [viewModal.visible]);

  if (!record) {return null;}

  const isActive = record?.status === 'ACTIVE';
  const tax = record?.tax || {};

  return (
    <Modal
      title={(
        <div className={'flex items-center gap-3'}>
          <span className={'text-lg font-bold text-gray-800'}>Product Details</span>
          <Tag
            icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            color={isActive ? 'success' : 'error'}
            className={' font-semibold'}
          >
            {record.status || 'UNKNOWN'}
          </Tag>
        </div>
      )}
      open={viewModal.visible}
      footer={null}
      width={860}
      onCancel={() => setViewModal({visible: false, record: null})}
      destroyOnClose
      styles={{
        body: {padding: '0', background: '#f8fafc'},
        header: {borderBottom: '1px solid #e2e8f0', paddingBottom: 12},
      }}
    >
      <div className={'bg-slate-50 p-5 md:p-6 space-y-5 rounded-b-lg'}>

        {/* HEADER: name + core info */}
        <div className={'bg-white rounded-lg flex justify-between shadow-sm border border-primary-100 p-5 space-y-4'}>
          <div>
            <h2 className={'text-2xl font-bold text-primary-900 leading-tight'}>
              {record.productName}
            </h2>
          
          </div>

          <div className={'px-6'}>
            <InfoRow
              icon={<BarcodeOutlined className={'text-xl text-black'} />}
              label={'EAN Code'}
              value={record.eanCode}
            />
          </div>
        </div>

        {/* STOCK + TAX */}
        <div className={'grid grid-cols-1 md:grid-cols-2 gap-5'}>

          {/* Stock */}
          <div className={'bg-white rounded-lg shadow-sm border border-primary-100 p-5'}>
            <h3 className={'font-bold text-base text-gray-700 flex items-center gap-2 mb-3'}>
              <InboxOutlined className={'text-blue-500'} /> Stock
            </h3>
            <div className={'flex items-center gap-3'}>
              <span className={`text-3xl font-bold ${record.quantity <= 5 ? 'text-red-500' : 'text-blue-600'}`}>
                {record.quantity ?? '—'}
              </span>
              <span className={'text-sm text-gray-400'}>units available</span>
              {record.quantity != null && record.quantity <= 5 && (
                <Tag color={'error'} className={'ml-auto text-xs'}>Low Stock</Tag>
              )}
            </div>
          </div>

          {/* Tax */}
          {(tax.cgst != null || tax.sgst != null || tax.igst != null) && (
            <div className={'bg-white rounded-lg shadow-sm border border-primary-100 p-5'}>
              <h3 className={'font-bold text-base text-gray-700 flex items-center gap-2 mb-3'}>
                <PercentageOutlined className={'text-blue-500'} /> Tax Breakdown
              </h3>
              <div className={'grid grid-cols-3 gap-2 text-center'}>
                {[
                  {label: 'CGST', val: tax.cgst},
                  {label: 'SGST', val: tax.sgst},
                  {label: 'IGST', val: tax.igst},
                ].map(({label, val}) => (
                  <div key={label} className={'bg-primary-50 rounded-md p-3'}>
                    <p className={'text-xs text-primary-400 font-semibold uppercase tracking-wide'}>{label}</p>
                    <p className={'text-lg font-bold text-primary-700'}>{val ?? 0}%</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* FEATURES */}
        {record.features?.length > 0 && (
          <div className={'bg-white rounded-lg shadow-sm border border-primary-100 p-5'}>
            <h3 className={'font-bold text-base text-gray-700 flex items-center gap-2 mb-3'}>
              <StarOutlined className={'text-blue-500'} /> Features
            </h3>
            <div className={'flex flex-wrap gap-2'}>
              {record.features.map((f, i) => (
                <Tag key={i} color={'default'} className={'text-sm px-3 py-1 rounded-full'}>
                  {f}
                </Tag>
              ))}
            </div>
          </div>
        )}

      </div>
    </Modal>
  );
};

ProductDetailScreen.propTypes = {
  viewModal: PropTypes.shape({
    visible: PropTypes.bool.isRequired,
    record: PropTypes.object,
  }).isRequired,
  setViewModal: PropTypes.func.isRequired,
};

export default ProductDetailScreen;
